import React, {
    useEffect, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    ChevronDown, Trash2, Plus, Loader2, Pencil, Settings2
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import LongTextField from '../../primitives/LongTextField';
import InlineOpcodeEditor from '../../opcode-editor/InlineOpcodeEditor';
import {
    BLANK_OPCODE_SEED
} from '../../opcode-editor/templates';

/**
 * OpCodesSection — assistant.opcodes[] (the behaviors). Each row: opcode_id +
 * routing fields (purpose / description). Click the title to inline-mount
 * the full opcode editor (`InlineOpcodeEditor`). Auto-syncs RAG-step
 * referenced collection_ids into assistant.knowledge[].
 *
 * The opcode-runtime-input ↔ typed-input mapping has its own dedicated
 * `InputMappingSection` (between Typed Input and OpCodes in the workspace);
 * this section no longer renders it per-row.
 */
export default function OpCodesSection({
    a, onPatch, opcodeMetaApi, inlineOpcodeRef, initialExpandedOpcodeId, onExpandedOpcodeConsumed,
    onExpandedOpcodeChange, onOpcodeSaved
}) {
    const {
        oragID, subOragID
    } = useParams();
    const opcodes = Array.isArray(a.opcodes) ? a.opcodes : [];

    // Shared cache provided by AssistantWorkspace so InputMappingSection sees
    // the same data we update on inline-save.
    const {
        meta: opcodeMeta, patchMeta, dropMeta
    } = opcodeMetaApi || {
        meta: {},
        patchMeta: () => {},
        dropMeta: () => {}
    };
    const [expanded, setExpanded] = useState(null); // opcode_id of inline-expanded card
    const [routingFor, setRoutingFor] = useState(null); // opcode_id with details-fields panel open
    const [creating, setCreating] = useState(false);
    const [createErr, setCreateErr] = useState(null);

    useEffect(() => {
        if (!initialExpandedOpcodeId) return;
        const inList = opcodes.some((o) => o?.opcode_id === initialExpandedOpcodeId);
        if (inList) {
            setExpanded(initialExpandedOpcodeId);
            onExpandedOpcodeConsumed?.();
        }
    }, [initialExpandedOpcodeId, opcodes, onExpandedOpcodeConsumed]);

    useEffect(() => {
        onExpandedOpcodeChange?.(expanded);
    }, [expanded, onExpandedOpcodeChange]);

    useEffect(() => () => onExpandedOpcodeChange?.(null), [onExpandedOpcodeChange]);

    function removeRow(i) {
        const removed = opcodes[i];
        onPatch({
            opcodes: opcodes.filter((_, j) => j !== i)
        });
        if (expanded === removed?.opcode_id) setExpanded(null);
        if (routingFor === removed?.opcode_id) setRoutingFor(null);
    }
    function patchRow(i, updates) {
        const next = [...opcodes];
        next[i] = {
            ...next[i],
            ...updates
        };
        onPatch({
            opcodes: next
        });
    }

    /** Create a blank opcode in the library and link it to this assistant. */
    async function createBlankOpcode() {
        setCreating(true);
        setCreateErr(null);
        try {
            const seed = {
                ...BLANK_OPCODE_SEED,
                title: opcodes.length ? `New opcode ${opcodes.length + 1}` : 'New opcode'
            };
            const res = await jsonOpcodeService.createJSONOpcode({
                organizationId: oragID,
                subOrganizationId: subOragID,
                opcode: seed
            });
            const newId = res?.data?.opcode_id || res?.opcode_id;
            if (!newId) throw new Error('Server did not return opcode_id.');
            opcodeMetaApi?.patchDoc?.(newId, res?.data || { ...seed, opcode_id: newId });
            onPatch({
                opcodes: [
                    ...opcodes,
                    {
                        opcode_id: newId,
                        purpose: seed.title || 'New opcode',
                        description: seed.description || '',
                        input_mapping: {}
                    }
                ]
            });
            setExpanded(newId);
        } catch (err) {
            setCreateErr(err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Create failed.');
        } finally {
            setCreating(false);
        }
    }

    /**
     * Called after inline editor save. Refreshes the meta cache for richer
     * card rendering, AND auto-merges any RAG-step-referenced collection_ids
     * into assistant.knowledge[] so the KB tab always reflects everything
     * the opcodes actually use (per UF: collections referenced by RAG steps
     * should appear under the KB tab automatically).
     */
    function refreshMeta(updated) {
        if (!updated?.opcode_id) return;
        const prev = opcodeMetaApi?.getDoc?.(updated.opcode_id) || {};
        opcodeMetaApi?.patchDoc?.(updated.opcode_id, {
            ...prev,
            ...updated,
            opcode_id: updated.opcode_id
        });
        patchMeta(updated.opcode_id, {
            title: updated.title || updated.opcode_id,
            description: updated.description || '',
            steps_count: updated.steps_count,
            kinds: updated.kinds,
            inputs: updated.inputs
        });

        const referenced = Array.isArray(updated.referencedCollections) ? updated.referencedCollections : [];
        if (referenced.length > 0) {
            const existing = Array.isArray(a.knowledge) ? a.knowledge : [];
            const existingIds = new Set(existing.map(e => e.collection_id));
            const additions = referenced
                .filter(cid => !existingIds.has(cid))
                .map(cid => ({
                    collection_id: cid,
                    description: `Auto-added: referenced by opcode "${updated.title || updated.opcode_id}".`
                }));
            if (additions.length > 0) {
                onPatch({
                    knowledge: [...existing, ...additions]
                });
            }
        }

        // Same auto-add pattern for api connections — every api_id referenced
        // by this opcode's steps (incl. branches, definitions, flows) gets
        // surfaced into assistant.data_connections so the engine's permission
        // whitelist stays in sync with what the opcode actually calls.
        const referencedApis = Array.isArray(updated.referencedApis) ? updated.referencedApis : [];
        if (referencedApis.length > 0) {
            const existingConns = Array.isArray(a.data_connections) ? a.data_connections : [];
            const existingConnIds = new Set(existingConns.map((c) => c.connection_id));
            const connAdditions = referencedApis
                .filter((id) => !existingConnIds.has(id))
                .map((id) => ({
                    connection_id: id,
                    description: `Auto-added: referenced by opcode "${updated.title || updated.opcode_id}".`
                }));
            if (connAdditions.length > 0) {
                onPatch({
                    data_connections: [...existingConns, ...connAdditions]
                });
            }
        }

        onOpcodeSaved?.();
    }

    /** Inline editor deleted the opcode → unlink it from the assistant. */
    function handleInlineDeleted(deletedId) {
        onPatch({
            opcodes: opcodes.filter(op => op.opcode_id !== deletedId)
        });
        setExpanded(null);
        dropMeta(deletedId);
    }

    return (
        <div className="space-y-3">
            {opcodes.length === 0 && (
                <div className="rounded-md border border-dashed bg-muted/20 p-6 text-center text-sm text-muted-foreground">
                    No opcodes yet. Add one to give this assistant something to do.
                </div>
            )}
            {opcodes.map((op, i) => {
                const meta = opcodeMeta[op.opcode_id] || {};
                const isOpen = expanded === op.opcode_id;
                const isRouting = routingFor === op.opcode_id;
                return (
                    <div key={op.opcode_id || i} className={cn(
                        'rounded-md border bg-card transition-colors',
                        isOpen && 'border-primary/40 ring-1 ring-primary/20'
                    )}>
                        <div className="flex items-start justify-between gap-3 p-3">
                            <button
                                type="button"
                                onClick={() => setExpanded(isOpen ? null : op.opcode_id)}
                                className="min-w-0 flex-1 text-left rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                                disabled={!op.opcode_id || meta.missing}
                                title={isOpen ? 'Click to close the editor' : 'Click to open this opcode for editing'}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="truncate font-semibold" title={meta.title || op.opcode_id}>
                                        {meta.title || op.opcode_id || '(unset opcode)'}
                                    </span>
                                    {meta.missing && (
                                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-800">
                                            Missing
                                        </span>
                                    )}
                                    {!meta.missing && meta.steps_count !== undefined && (
                                        <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                                            {meta.steps_count} {meta.steps_count === 1 ? 'step' : 'steps'}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                                    {op.purpose || meta.description || 'No purpose set.'}
                                </div>
                                {meta.kinds && meta.kinds.length > 0 && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {meta.kinds.map(k => (
                                            <span key={k} className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                                                {k}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </button>
                            <div className="flex flex-shrink-0 items-center gap-1">
                                <Button
                                    variant={isOpen ? 'secondary' : 'default'}
                                    size="sm"
                                    onClick={() => setExpanded(isOpen ? null : op.opcode_id)}
                                    disabled={!op.opcode_id || meta.missing}
                                    title={isOpen ? 'Close editor' : 'Open opcode editor'}
                                >
                                    {isOpen ? (
                                        <>
                                            <ChevronDown className="mr-1 h-3.5 w-3.5 rotate-180" />
                                            Close
                                        </>
                                    ) : (
                                        <>
                                            <Pencil className="mr-1 h-3.5 w-3.5" />
                                            Open
                                        </>
                                    )}
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setRoutingFor(isRouting ? null : op.opcode_id)}
                                    title="Routing & metadata (purpose, description, ID)"
                                >
                                    <Settings2 className="mr-1 h-3.5 w-3.5" />
                                    Routing
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive"
                                    onClick={() => removeRow(i)}
                                    title="Remove from this assistant"
                                >
                                    <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                        </div>

                        {/* Optional: routing + metadata (purpose / description / opcode_id).
                            The opcode↔assistant input mapping has its own dedicated
                            section above (Input Mapping, between Typed Input and OpCodes). */}
                        {isRouting && (
                            <div className="space-y-3 border-t bg-muted/20 px-3 py-3">
                                <div>
                                    <Label>Purpose (drives intent routing)</Label>
                                    <Input
                                        value={op.purpose || ''}
                                        onChange={(e) => patchRow(i, {
                                            purpose: e.target.value
                                        })}
                                        placeholder="When the user wants to..."
                                    />
                                </div>
                                <div>
                                    <Label>Description (for documentation)</Label>
                                    <LongTextField
                                        label="Description"
                                        value={op.description || ''}
                                        onChange={(v) => patchRow(i, {
                                            description: v
                                        })}
                                        monospace={false}
                                        previewLines={2}
                                    />
                                </div>
                                <div>
                                    <Label>OpCode ID (server-generated)</Label>
                                    <Input value={op.opcode_id || ''} disabled className="font-mono text-[12.5px]" />
                                </div>
                            </div>
                        )}

                        {/* Inline opcode editor */}
                        {isOpen && !meta.missing && (
                            <div className="border-t bg-background p-3">
                                <InlineOpcodeEditor
                                    ref={inlineOpcodeRef}
                                    opcodeId={op.opcode_id}
                                    initialDoc={opcodeMetaApi?.getDoc?.(op.opcode_id)}
                                    onSaved={refreshMeta}
                                    onDeleted={handleInlineDeleted}
                                    onLiveInputsChange={(opcId, nextInputs) => patchMeta(opcId, {
                                        inputs: nextInputs
                                    })}
                                />
                            </div>
                        )}
                    </div>
                );
            })}

            <div className="flex flex-wrap items-center gap-2">
                <Button onClick={createBlankOpcode} disabled={creating}>
                    {creating ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Plus className="mr-1 h-3 w-3" />}
                    New opcode
                </Button>
                <span className="text-[11px] text-muted-foreground">
                    Creates a blank opcode in this sub-org's Library and opens it inline.
                </span>
                {createErr && (
                    <span className="text-[11px] text-red-700">{createErr}</span>
                )}
            </div>

            <div>
                <Label>Default opcode (used when intent routing can't match)</Label>
                <select
                    value={a.default_opcode || ''}
                    onChange={(e) => onPatch({
                        default_opcode: e.target.value
                    })}
                    className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm"
                >
                    <option value="">— none —</option>
                    {opcodes.map((op) => (
                        <option key={op.opcode_id} value={op.opcode_id}>
                            {opcodeMeta[op.opcode_id]?.title || op.opcode_id}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
}
